import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import apiDefinitions from "../../../../../core/api/apiDefinitions";
import { checkPermission } from "../../../../../core/permissons/CheckPermissons";

/* ---------- Props & DTOs ---------- */
interface Props {
  show: boolean;
  onHide: () => void;
  vehicleId: string;
  onAllocated: (slotName: string) => void; // returns the chosen slot name on success
}

interface ParkingSlotDTO {
  id: number;
  slotName: string;
  status: string;
  locationId?: number | null;
  floorId?: number | null;
  locationName?: string | null;
  floorName?: string | null;
  x?: string;
  y?: string;
}

interface LocationDTO {
  id: number;
  name: string;
}

interface BuildingDTO {
  id: number;
  name: string;
}

interface FloorDTO {
  id: number;
  name: string;
}

/* Natural-ish sort for names like A1, A2, A10 */
const naturalSort = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

/* ==========================================================
   Component
========================================================== */
const AssignParkingSlotModal: React.FC<Props> = ({ show, onHide, vehicleId, onAllocated }) => {
  const [slots, setSlots] = useState<ParkingSlotDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [locations, setLocations] = useState<LocationDTO[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | "">("");
  const [buildings, setBuildings] = useState<BuildingDTO[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | "">("");
  const [floors, setFloors] = useState<FloorDTO[]>([]);
  const [selectedFloorId, setSelectedFloorId] = useState<number | "">("");

  const canViewSlots = checkPermission("ROLE_GET_PARKING_SLOT");
  const canGetLocations = checkPermission("ROLE_GET_LOCATIONS");
  const canViewFloors = checkPermission("ROLE_VIEW_FLOOR");

  /* -------------------- Loaders -------------------- */
  const fetchLocations = async () => {
    if (!canGetLocations) return;
    try {
      const payload = { filterType: "", filterValue: "", date: "" };
      const res = await apiDefinitions.getLocations("1", payload);
      const data: LocationDTO[] = (res?.data?.data?.locations || []) as LocationDTO[];
      data.sort((a, b) => naturalSort(a.name, b.name));
      setLocations(data);
    } catch (e) {
      // non-fatal for this modal; user can still fetch all slots
    }
  };

  const fetchBuildings = async (locationId: number) => {
    try {
      const res = await apiDefinitions.getBuildingsByLocationId(locationId);
      const data: BuildingDTO[] = (res?.data?.data || []) as BuildingDTO[];
      data.sort((a, b) => naturalSort(a.name, b.name));
      setBuildings(data);
    } catch (e) {
      setBuildings([]);
    }
  };

  const fetchFloors = async (buildingId: number) => {
    if (!canViewFloors) {
      setFloors([]);
      return;
    }
    try {
      const res = await apiDefinitions.getFloorsByBuildingId(buildingId);
      const data: FloorDTO[] = (res?.data?.data || []) as FloorDTO[];
      data.sort((a, b) => naturalSort(a.name, b.name));
      setFloors(data);
    } catch (e) {
      setFloors([]);
    }
  };

  const fetchAvailableSlots = async (opts?: { locationId?: number; buildingId?: number; floorId?: number }) => {
    if (!canViewSlots) {
      setError("You do not have permission to view parking slots.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Build filter payload by priority: floorId > buildingId > locationId
      let payload: { filterType: string; filterValue: string; date: string } = {
        filterType: "",
        filterValue: "",
        date: "",
      };

      if (opts?.floorId) {
        payload = { filterType: "floorId", filterValue: String(opts.floorId), date: "" };
      } else if (opts?.buildingId) {
        payload = { filterType: "buildingId", filterValue: String(opts.buildingId), date: "" };
      } else if (opts?.locationId) {
        payload = { filterType: "locationId", filterValue: String(opts.locationId), date: "" };
      }

      const res = await apiDefinitions.getAllParkingSlots("1", payload);
      const data: ParkingSlotDTO[] = (res?.data?.data || []) as ParkingSlotDTO[];
      const onlyAvailable = (data || []).filter((s) => (s.status || "").toUpperCase() === "AVAILABLE");

      // Try to enrich names if missing by using current selection lists
      const enriched = onlyAvailable.map((s) => {
        const out = { ...s };
        if (!out.locationName && out.locationId) {
          out.locationName = locations.find((l) => l.id === out.locationId)?.name ?? out.locationName ?? null;
        }
        if (!out.floorName && out.floorId) {
          out.floorName = floors.find((f) => f.id === out.floorId)?.name ?? out.floorName ?? null;
        }
        return out;
      });

      enriched.sort((a, b) => naturalSort(a.slotName, b.slotName));
      setSlots(enriched);
    } catch (e) {
      setError("Failed to fetch parking slots.");
      setSlots([]);
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- Effects -------------------- */
  useEffect(() => {
    if (show) {
      setQuery("");
      setSelectedSlotId(null);
      setSelectedLocationId("");
      setSelectedBuildingId("");
      setSelectedFloorId("");
      fetchLocations();
      // initial fetch without filters
      fetchAvailableSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // When location changes → reset down-stream, fetch buildings, refresh slots
  useEffect(() => {
    if (selectedLocationId === "") {
      setBuildings([]);
      setSelectedBuildingId("");
      setFloors([]);
      setSelectedFloorId("");
      // Unfiltered OR only by location? We'll fetch by location only when user selects one (handled below)
      fetchAvailableSlots();
      return;
    }
    fetchBuildings(Number(selectedLocationId));
    setSelectedBuildingId("");
    setFloors([]);
    setSelectedFloorId("");
    fetchAvailableSlots({ locationId: Number(selectedLocationId) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId]);

  // When building changes → reset floors, fetch floors, refresh slots
  useEffect(() => {
    if (selectedBuildingId === "") {
      setFloors([]);
      setSelectedFloorId("");
      // If location exists, keep location filter; else unfiltered
      if (selectedLocationId !== "") {
        fetchAvailableSlots({ locationId: Number(selectedLocationId) });
      } else {
        fetchAvailableSlots();
      }
      return;
    }
    fetchFloors(Number(selectedBuildingId));
    setSelectedFloorId("");
    fetchAvailableSlots({
      buildingId: Number(selectedBuildingId),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuildingId]);

  // When floor changes → refresh slots
  useEffect(() => {
    if (selectedFloorId === "") {
      // fall back to building → else location → else none
      if (selectedBuildingId !== "") {
        fetchAvailableSlots({ buildingId: Number(selectedBuildingId) });
      } else if (selectedLocationId !== "") {
        fetchAvailableSlots({ locationId: Number(selectedLocationId) });
      } else {
        fetchAvailableSlots();
      }
      return;
    }
    fetchAvailableSlots({ floorId: Number(selectedFloorId) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFloorId]);

  /* -------------------- Search filter -------------------- */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return slots;
    return slots.filter((s) => s.slotName.toLowerCase().includes(q));
  }, [slots, query]);

  /* -------------------- Allocate -------------------- */
  const handleAllocate = async () => {
    if (!selectedSlotId) {
      setError("Please select a slot to allocate.");
      return;
    }
    try {
      await apiDefinitions.allocateParkingSlotPermanently(selectedSlotId, vehicleId);
      const slot = slots.find((s) => s.id === selectedSlotId);
      onAllocated(slot?.slotName || "Allocated");
    } catch (e) {
      setError("Failed to allocate parking slot.");
    }
  };

  /* -------------------- Render -------------------- */
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Allocate Parking Slot</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Filters */}
        <div className="row g-2">
          <div className="col-12 col-md-4">
            <Form.Label>Location</Form.Label>
            <Form.Select
              value={selectedLocationId}
              onChange={(e) => setSelectedLocationId(e.target.value ? Number(e.target.value) : "")}
              disabled={!canGetLocations}
            >
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </Form.Select>
          </div>

          <div className="col-12 col-md-4">
            <Form.Label>Building</Form.Label>
            <Form.Select
              value={selectedBuildingId}
              onChange={(e) => setSelectedBuildingId(e.target.value ? Number(e.target.value) : "")}
              disabled={!selectedLocationId}
            >
              <option value="">All buildings</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </Form.Select>
          </div>

          <div className="col-12 col-md-4">
            <Form.Label>Floor</Form.Label>
            <Form.Select
              value={selectedFloorId}
              onChange={(e) => setSelectedFloorId(e.target.value ? Number(e.target.value) : "")}
              disabled={!selectedBuildingId || !canViewFloors}
            >
              <option value="">All floors</option>
              {floors.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </Form.Select>
          </div>
        </div>

        {/* Search */}
        <div className="mt-3">
          <Form.Label>Search</Form.Label>
          <Form.Control
            type="text"
            placeholder="Search by slot name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {/* Loading / Errors / Results */}
        {loading ? (
          <div className="text-center py-3">Loading slots…</div>
        ) : (
          <>
            {error && <div className="alert alert-danger py-2 mt-3">{error}</div>}

            {filtered.length === 0 ? (
              <div className="alert alert-info mt-3">No available parking slots found.</div>
            ) : (
              <div className="table-responsive mt-3" style={{ maxHeight: 360 }}>
                <table className="table table-sm table-hover align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 48 }}></th>
                      <th>Slot Name</th>
                      <th>Location / Floor</th>
                      <th>Coords</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <Form.Check
                            type="radio"
                            name="slotPicker"
                            checked={selectedSlotId === s.id}
                            onChange={() => setSelectedSlotId(s.id)}
                          />
                        </td>
                        <td className="fw-semibold">{s.slotName}</td>
                        <td className="text-muted small">{s.floorName || s.locationName || "-"}</td>
                        <td className="text-muted small">{s.x && s.y ? `(${s.x}, ${s.y})` : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>Cancel</Button>
        <Button variant="success" onClick={handleAllocate} disabled={!selectedSlotId || !canViewSlots}>
          Allocate
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignParkingSlotModal;
